import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { useApp } from '../../lib/app-context';
import { instant, mad } from '../../lib/format';
import { Badge, CopyValue, EmptyState, Field, Spinner, TextInput } from '../../components/ui';
import { t } from '../../lib/i18n';

/**
 * Affiliates: partners who bring people to Bedly with a code.
 *
 * Each one gets a code and a ready-made link (bedly.ma/inscription?ref=CODE);
 * anyone who signs up with it is attributed for good. Pausing an affiliate
 * stops new attributions without erasing the ones already made.
 */

type AffiliateRow = {
  id: string;
  code: string;
  name: string;
  note: string | null;
  active: boolean;
  createdAt: string;
  stats: {
    signups: number;
    verified: number;
    signupsLast30Days: number;
    confirmedBookings: number;
    feesCentimes: number;
  };
};

type Referred = {
  id: string;
  fullName: string;
  email: string | null;
  createdAt: string;
  verified: boolean;
  isHost: boolean;
  bookings: number;
};

const linkFor = (code: string) => `${window.location.origin}/inscription?ref=${encodeURIComponent(code)}`;

export default function AdminAffiliates() {
  const { toast, reportError } = useApp();
  const [rows, setRows] = useState<AffiliateRow[] | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [referred, setReferred] = useState<Referred[] | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get<{ affiliates: AffiliateRow[] }>('/admin/affiliates');
      setRows(response.affiliates);
    } catch (error) {
      reportError(error);
    }
  }, [reportError]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    setErrors({});
    try {
      const response = await api.post<{ affiliate: { code: string } }>('/admin/affiliates', {
        name,
        ...(code.trim() ? { code: code.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast(t('Affilié créé — code {code}', { code: response.affiliate.code }), 'ok');
      setName('');
      setCode('');
      setNote('');
      await load();
    } catch (error) {
      const apiError = reportError(error);
      if (apiError instanceof ApiError) setErrors(apiError.fieldErrors());
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (row: AffiliateRow) => {
    try {
      await api.patch(`/admin/affiliates/${row.id}`, { active: !row.active });
      toast(row.active ? t("Affilié mis en pause") : t("Affilié réactivé"), 'ok');
      await load();
    } catch (error) {
      reportError(error);
    }
  };

  const showUsers = async (id: string) => {
    if (openId === id) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    setReferred(null);
    try {
      const response = await api.get<{ users: Referred[] }>(`/admin/affiliates/${id}/users`);
      setReferred(response.users);
    } catch (error) {
      reportError(error);
    }
  };

  return (
    <div className="stack stack--loose">
      <section className="card card--pad stack">
        <h2 style={{ margin: 0 }}>{t("Nouvel affilié")}</h2>
        <div className="row row--wrap" style={{ gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 220px' }}>
            <Field label={t("Nom")} error={errors.name}>
              <TextInput value={name} onChange={setName} placeholder={t("Club étudiant UM5, @influenceuse…")} />
            </Field>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <Field label={t("Code (optionnel)")} hint={t("Généré si vide")} error={errors.code}>
              <TextInput value={code} onChange={(value) => setCode(value.toUpperCase())} placeholder={t("UM5-2026")} />
            </Field>
          </div>
        </div>
        <Field label={t("Note interne (optionnel)")}>
          <TextInput value={note} onChange={setNote} placeholder={t("Conditions, contact, commission convenue…")} />
        </Field>
        <button className="btn" disabled={busy || name.trim().length < 2} onClick={create}>
          {busy ? t("Création…") : t("Créer l’affilié")}
        </button>
      </section>

      {rows === null ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t("Aucun affilié pour l’instant")}
          description={t("Créez un code pour un partenaire : chaque inscription faite avec lui sera suivie ici.")}
        />
      ) : (
        <div className="stack">
          {rows.map((row) => (
            <div key={row.id} className="card card--pad stack stack--tight">
              <div className="row row--between row--wrap" style={{ gap: 10 }}>
                <div className="stack" style={{ gap: 2 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <strong style={{ fontSize: 16 }}>{row.name}</strong>
                    <Badge tone={row.active ? 'badge--ok' : 'badge--warn'}>
                      {row.active ? t("Actif") : t("En pause")}
                    </Badge>
                  </div>
                  <span className="tiny muted">
                    {t("Code")} <strong>{row.code}</strong> {t("· créé le")} {instant(row.createdAt)}
                    {row.note ? ` · ${row.note}` : ''}
                  </span>
                </div>
                <div className="action-row action-row--quiet">
                  <button className="btn btn--ghost btn--sm" onClick={() => void showUsers(row.id)}>
                    {openId === row.id ? t("Masquer les inscrits") : t("Voir les inscrits")}
                  </button>
                  <button className="btn btn--quiet btn--sm" onClick={() => void toggle(row)}>
                    {row.active ? t("Mettre en pause") : t("Réactiver")}
                  </button>
                </div>
              </div>

              <div className="kpis">
                {[
                  [t("Inscrits"), row.stats.signups],
                  [t("Vérifiés"), row.stats.verified],
                  [t("30 derniers jours"), row.stats.signupsLast30Days],
                  [t("Réservations"), row.stats.confirmedBookings],
                  [t("Frais générés"), mad(row.stats.feesCentimes)],
                ].map(([label, value]) => (
                  <div key={String(label)} className="kpi" style={{ padding: '6px 0' }}>
                    <span className="tiny muted">{label}</span>
                    <span style={{ fontSize: 18, fontWeight: 800 }}>{value}</span>
                  </div>
                ))}
              </div>

              <CopyValue label={t("Lien d’inscription")} value={linkFor(row.code)} />

              {openId === row.id ? (
                referred === null ? (
                  <Spinner />
                ) : referred.length === 0 ? (
                  <p className="small muted" style={{ margin: 0 }}>{t("Personne ne s’est encore inscrit avec ce code.")}</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table--stack">
                      <thead>
                        <tr>
                          <th>{t("Membre")}</th>
                          <th>{t("Inscrit le")}</th>
                          <th>{t("Email")}</th>
                          <th>{t("Réservations")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referred.map((user) => (
                          <tr key={user.id}>
                            <td data-label={t("Membre")}>
                              {user.fullName}
                              {user.isHost ? <span className="tiny muted"> {t("· hôte")}</span> : null}
                            </td>
                            <td className="tiny" data-label={t("Inscrit le")}>{instant(user.createdAt)}</td>
                            <td className="tiny" data-label={t("Email")}>
                              {user.email ?? '—'} {user.verified ? '✓' : ''}
                            </td>
                            <td data-label={t("Réservations")}>{user.bookings}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
